import React, { useState } from 'react';
import { IWorkoutFeedback } from '../../../types/workout/workout-feedback';
import { PD, SF, SFm } from './tokens';
import { formatCompletedOn } from './helpers';

interface DotScaleProps {
  value: number | null | undefined;
  color: string;
}

const DotScale: React.FC<DotScaleProps> = ({ value, color }) => {
  const dots: React.ReactNode[] = [];
  for (let i = 0; i < 5; i++) {
    const threshold = (i + 1) * 2;
    const lit = value != null && value >= threshold;
    const half = value != null && !lit && value > i * 2;
    dots.push(
      <span
        key={i}
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: lit ? color : half ? color : PD.fillStrong,
          opacity: lit ? 1 : half ? 0.55 : 1
        }}
      />
    );
  }
  return <div style={{ display: 'flex', gap: 3 }}>{dots}</div>;
};

interface MetricInlineProps {
  label: string;
  value: number | null | undefined;
  color: string;
  max?: number;
}

const MetricInline: React.FC<MetricInlineProps> = ({ label, value, color, max = 10 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span
        style={{
          fontFamily: SF,
          fontSize: 10.5,
          fontWeight: 700,
          color: PD.ink3,
          letterSpacing: 0.4,
          textTransform: 'uppercase'
        }}
      >
        {label}
      </span>
      <span style={{ fontFamily: SFm, fontSize: 13, fontWeight: 600, color: PD.ink }}>
        {value != null ? value : '—'}
        <span style={{ color: PD.ink3 }}>{value != null ? `/${max}` : ''}</span>
      </span>
    </div>
    <DotScale value={value} color={color} />
  </div>
);

interface FeedbackStripProps {
  feedback: IWorkoutFeedback;
}

const FeedbackStrip: React.FC<FeedbackStripProps> = ({ feedback }) => {
  const [expanded, setExpanded] = useState(false);
  const completedOn = formatCompletedOn(feedback.realEndDate);
  const hasComment = Boolean(feedback.generalFeedback || feedback.additionalNotes);

  return (
    <div
      style={{
        background: 'linear-gradient(180deg, rgba(34, 197, 94, 0.04), rgba(34, 197, 94, 0))',
        borderBottom: `0.5px solid ${PD.hairline}`,
        flex: '0 0 auto'
      }}
    >
      <div
        style={{
          padding: '12px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 22,
          flexWrap: 'wrap'
        }}
      >
        {/* Duration block */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              fontFamily: SFm,
              fontSize: 22,
              fontWeight: 600,
              color: PD.ink,
              letterSpacing: '-0.02em',
              lineHeight: 1
            }}
          >
            {feedback.sessionTime || '—'}
          </div>
          <div style={{ fontFamily: SF, fontSize: 11, fontWeight: 500, color: PD.ink2, marginTop: 4 }}>
            {completedOn ? `Completado ${completedOn}` : 'Completado'}
          </div>
        </div>

        {/* Vertical hairline */}
        <div style={{ width: 0.5, alignSelf: 'stretch', background: PD.divider, margin: '2px 0' }} />

        {/* Metrics */}
        <div style={{ display: 'flex', gap: 22, flex: 1, flexWrap: 'wrap' }}>
          <MetricInline label="Ánimo" value={feedback.mood ?? null} color={PD.success} />
          <MetricInline label="Energía" value={feedback.energyLevel ?? null} color={PD.limeInk} />
          <MetricInline label="Dificultad" value={feedback.perceivedDifficulty ?? null} color={PD.warn} />
        </div>

        {/* Comment toggle */}
        {hasComment && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={{
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 11px',
              borderRadius: 999,
              border: `0.5px solid ${PD.cardEdge}`,
              background: PD.card,
              cursor: 'pointer',
              fontFamily: SF,
              fontSize: 12,
              fontWeight: 600,
              color: PD.ink2
            }}
          >
            <i className="pi pi-comment" style={{ fontSize: 11 }} />
            {expanded ? 'Ocultar' : 'Comentario'}
            <i
              className={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}
              style={{ fontSize: 10, color: PD.ink3 }}
            />
          </button>
        )}
      </div>

      {/* Expanded comment */}
      {hasComment && (
        <div className={`pd-fb-comment${expanded ? ' is-open' : ''}`}>
          <div style={{ padding: '0 18px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {feedback.generalFeedback && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div
                  style={{
                    fontFamily: SF,
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: PD.ink3,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    flex: '0 0 90px',
                    marginTop: 1
                  }}
                >
                  Cómo se sintió
                </div>
                <div style={{ flex: 1, fontFamily: SF, fontSize: 13.5, color: PD.ink, lineHeight: 1.45 }}>
                  {feedback.generalFeedback}
                </div>
              </div>
            )}
            {feedback.additionalNotes && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div
                  style={{
                    fontFamily: SF,
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: PD.ink3,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    flex: '0 0 90px',
                    marginTop: 1
                  }}
                >
                  Notas extra
                </div>
                <div style={{ flex: 1, fontFamily: SF, fontSize: 13.5, color: PD.ink, lineHeight: 1.45 }}>
                  {feedback.additionalNotes}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackStrip;
